import React, { useEffect, useState, useCallback } from 'react';
import { withStyles } from '@mui/styles';
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import axiosInstance from 'utils/axiosUtil';
import PropTypes from 'prop-types';
import { PayorSelectDialog, formatPayorName } from './PayorSelectDialog';

// Same look as OrganizationSelect beside it: a Select that never opens its own menu,
// wrapped in a Button that opens the picker dialog instead.
const styles = () => ({
  container: {
    width: '35rem',
    maxWidth: '100%',
  },
  button: {
    padding: 0,
    textTransform: 'none',
    width: '100%',
  },
  select: {
    width: '100%',
  },
});

function PayorSearch(props) {
  // Defaulted here rather than via defaultProps: React 19 ignores defaultProps on function
  // components, so an omitted `limit` would reach PayorSelectDialog as undefined and silently
  // disable its selection cap. Every limit below derives from this one value — hardcoding 3 in
  // the fetches while passing `limit` to the dialog let the user pick more payors than the
  // rehydration fetch would return, silently dropping the extras.
  const limit = props.limit ?? 3;
  const { classes, initialNames, ready } = props;
  const [payors, setPayors] = useState([]);
  const [loadedPayorIds, setLoadedPayorIds] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  useEffect(() => {
    if (
      initialNames &&
      ready &&
      props.payorIds?.length === 0 &&
      loadedPayorIds.length === 0
    ) {
      (async function () {
        const query = {
          where: {
            $and: {
              $or: [
                ...initialNames.slice(0, limit).map((name) => ({ name: name })),
              ],
              claims_type: { $ne: null },
            },
          },
          limit,
        };

        const response = await axiosInstance(
          `/Payers?opts=${encodeURIComponent(JSON.stringify(query))}`,
        );
        setPayors(response.data);
        props.onPayorsChanged?.(response.data);
      })();
    }
  }, [ready]);
  useEffect(() => {
    if (!ready) return;
    if (
      !props.payorIds?.filter((p) => !!p)?.length ||
      props.payorIds.every((id) => loadedPayorIds.includes(id))
    ) {
      return;
    }
    (async function () {
      const query = {
        where: {
          $and: {
            $or: { id: props.payorIds },
            claims_type: { $ne: null },
          },
        },
        limit,
      };

      const response = await axiosInstance(
        `/Payers?opts=${encodeURIComponent(JSON.stringify(query))}`,
      );
      const orderedPayors = props.payorIds
        .map((id) => response.data.find((p) => id === p.id))
        .filter((p) => !!p);
      setLoadedPayorIds(orderedPayors.map((p) => p.id));
      setPayors(orderedPayors);
      props.onPayorsChanged?.(orderedPayors);
    })();
  }, [props.payorIds, ready]);

  const onSave = useCallback((selected) => {
    setDialogOpen(false);
    setPayors(selected);
    props.onPayorsChanged?.(selected);
  }, [props.onPayorsChanged]);

  const label = payors.length > 0
    ? payors.map((p) => formatPayorName(p.name)).join(', ')
    : 'Select payors';

  return (
    <div className={classes.container}>
      <FormControl variant='outlined' fullWidth>
        <InputLabel>Payors</InputLabel>
        <Button
          onClick={() => setDialogOpen(true)}
          className={classes.button}
          disableRipple
        >
          <Select
            value='custom'
            label='Payors'
            open={false}
            className={classes.select}
            renderValue={() => (
              <Typography align='left' noWrap>
                {label}
              </Typography>
            )}
          >
            <MenuItem value='custom' />
          </Select>
        </Button>
      </FormControl>
      {dialogOpen && (
        <PayorSelectDialog
          open={dialogOpen}
          initialSelected={payors}
          maxSelected={limit}
          onClose={() => setDialogOpen(false)}
          onSave={onSave}
        />
      )}
    </div>
  );
}
PayorSearch.propTypes = {
  classes: PropTypes.object.isRequired,
  onPayorsChanged: PropTypes.func,
  initialNames: PropTypes.array,
  payorIds: PropTypes.array,
  ready: PropTypes.bool,
  limit: PropTypes.number,
};
const styled = withStyles(styles)(PayorSearch);
export { styled as PayorSearch };
